import { last } from 'lodash';
import React, { useState, useRef } from 'react';

import { api } from 'services/api';
import { session } from 'services/session';
import { Loader } from 'components/misc/loader-utils';

import filecsv from 'images/icons/filecsv.svg';
import exportSVG from 'images/icons/export-dark.svg';
import './product-modal.scss';

const csvContent = 'data:text/csv;charset=utf-8,'
	+ 'sku1\r\nsku2\r\nsku3\r\nsku4\r\nsku5';
const encodedUri = encodeURI(csvContent);

const errorMap = {
	missing_ids: 'Missing SKU ID',
	bad_formats: 'Incorrect SKU ID format',
	missing_matches: 'No matches for SKU ID',
};

const genericError = 'There was an error uploading your file.'
	+ ' Please check the file type and try again.';

function ProductModal({ triggers, setShowModal, confirmTriggers }) {
	/* State */
	const [stats, setStats] = useState([]);
	const [selectedFile, setFile] = useState(null);
	const [modalStep, setModalStep] = useState('upload');
	const [uploadError, setUploadError] = useState(null);
	const [errors, setErrors] = useState({});
	const [errorCSV, setErrorCSV] = useState('');
	const [duplicates, setDuplicates] = useState(0);
	const [apiError, setErrorMessage] = useState(null);
	const [loading, setLoading] = useState(false);

	/* Constants */
	const endpoint = `/api/v0/merchants/${session.orgId}/strategy/validate_products/`;

	/* Methods */
	function queryValidation() {
		setLoading(true);

		const formData = new FormData();
		formData.append('file', selectedFile);

		api.post(endpoint, formData).then((resp) => {
			const data = resp.data.data[0];
			const [stats, duplicates] = dedupStats(data.stats);
			const totalErrors = Object.values(data.errors).reduce((agg, val) => {
				return agg += val;
			}, 0);
			data.errors.total = totalErrors;

			setStats(stats);
			setErrors(data.errors);
			setErrorCSV(data.error_csv);
			setDuplicates(duplicates);
			setModalStep('confirm');
			setLoading(false);
		}).catch((e) => {
			const message = e && e.message ? e.message : genericError;
			setErrorMessage(message);
			setLoading(false);
		});
	}

	function dedupStats(stats) {
		let duplicates = 0;
		const dupDict = {};
		const newTriggers = [];

		triggers.forEach((item) => dupDict[item.sku] = true);
		stats.forEach((item) => {
			if (!dupDict[item.sku]) {
				dupDict[item.sku] = true;
				newTriggers.push(item);
			} else {
				duplicates++;
			}
		});

		return [newTriggers, duplicates];
	}

	function onFileUpload(e, type) {
		e.preventDefault();
		e.stopPropagation();

		const file = type === 'drop'
			? e.dataTransfer.files[0]
			: e.target.files[0];

		if (!file) return;

		const extension = last(file.name.split('.'));

		if (extension !== 'csv') {
			setErrorMessage(genericError);
		} else {
			setErrorMessage(null);
			setFile(file);
		}
	}

	function preventDefaults(e) {
		e.preventDefault();
		e.stopPropagation();
	}

	function buttonOnClick() {
		if (modalStep === 'upload' && selectedFile) {
			queryValidation();
		}

		if (modalStep === 'confirm') {
			confirmTriggers(stats, errors.total);
			setShowModal(false);
		}
	}

	/* Render */
	const fileButton = (
		<>
			<input
				className="file-btn"
				name="file"
				type="file"
				id="file"
				onChange={onFileUpload}
			/>
			<label
				className="file-label"
				htmlFor="file">
				{selectedFile ? 'Replace file' : 'Add File'}
			</label>
		</>
	);

	const importContent = selectedFile ? (
		<div className="display-file">
			<div className="title">Added file:</div>
			<div className="file-display">
				<div className="name">
					<img src={filecsv} alt="file icon" />
					{selectedFile.name}
				</div>
				<div className="button">
					{fileButton}
				</div>
			</div>
		</div>
	) : (
		<div
			className="import-box"
			onDragEnter={preventDefaults}
			onDragLeave={preventDefaults}
			onDragOver={preventDefaults}
			onDrop={(e) => onFileUpload(e, 'drop')}>
			{fileButton}
			<div className="subtext">or drop files to upload (.csv)</div>
		</div>
	);

	const buttons = (
		<div className="buttons">
			<div className="cancel"
				onClick={() => setShowModal(false)}>
				Cancel
			</div>
			<div className="upload"
				onClick={buttonOnClick}>
				{modalStep === 'upload'
					? 'Upload and continue'
					: `Add ${stats.length} Matched Products`}
			</div>
		</div>
	);

	const errorContent = apiError ? (
		<div className="upload-error">
			{apiError}
		</div>
	) : null;

	const statRows = stats.map((stat, i) => {
		return (
			<div key={`statRow-${i}`} className="row">
				<div className="sku">{stat.sku}</div>
				<div className="name">{stat.name}</div>
				<div className="brand">{stat.brand}</div>
			</div>
		);
	});

	const errorRows = Object.keys(errors).map((key, i, arr) => {
		if (i === arr.length - 1) return null;
		return (
			<div key={`errorRow-${i}`} className="row">
				<div className="error">{errorMap[key]}</div>
				<div className="affected">{errors[key]}</div>
			</div>
		);
	});

	const importProducts = modalStep === 'upload' ? (
		<div className="import-products">
			<div className="header">Import Products by CSV</div>
			<div className="sample">
				Download a
				<a href={encodedUri}
					target="_blank"
					rel="noopener noreferrer"
					download="sample-csv.csv">
					sample CSV template
				</a>
				to see an example of the format required.
			</div>
			{errorContent}
			{loading ? null : importContent}
			<Loader loading={loading} />
			{buttons}
		</div>
	) : null;

	const encodedErrorCSV = encodeURI(errorCSV);
	const download = modalStep === 'confirm' && errorCSV ? (
		<div className="download">
			<div>Download a CSV containing the affected rows. Fix errors and then reupload.</div>
			<div>
				<a href={encodedErrorCSV}
					target="_blank"
					rel="noopener noreferrer"
					download="error-log-csv.csv">
					<img src={exportSVG} alt="download" />
					<span>Download Error Log CSV</span>
				</a>
			</div>
		</div>
	) : null;

	const reviewProducts = modalStep === 'confirm' ? (
		<div className="confirm-products">
			<div className="main-header">
				<div className="title">Review CSV Data</div>
				<div className="summary">
					We successfully matched <span>{stats.length}</span> out
					of <span>{stats.length + errors.total}</span> SKU IDs to Products.
					We detected <span className="error">{errors.total} errors</span> in your file.
				</div>
			</div>
			<div className="stats">
				<div className="header">{stats.length} Matches</div>
				<div className="table">
					<div className="header-row">
						<div className="sku">SKU ID</div>
						<div className="name">Product Name</div>
						<div className="brand">Brand</div>
					</div>
					{statRows}
				</div>
			</div>
			<div className="errors">
				<div className="header">{errors.total} Errors</div>
				{download}
				<div className="table">
					<div className="header-row">
						<div className="error">Error</div>
						<div className="affected">Rows Affected</div>
					</div>
					{errorRows}
				</div>
			</div>
			{buttons}
		</div>
	) : null;

	const smallScreen = window.innerHeight < 800 ? 'small-screen' : '';

	return (
		<div className="product-modal">
			<div
				className={`product-modal-content ${modalStep} ${smallScreen}`}>
				{importProducts}
				{reviewProducts}
			</div>
		</div>
	);
}

export default ProductModal;
